import { type NegotiationEntity, NegotiationOutcome, NegotiationState } from '~/apiClient';

export enum NegotiationUiStatus {
    Successful = 'Successful',
    InProgress = 'InProgress',
    Unsuccessful = 'Unsuccessful',
}

export type ExtendedNegotiation<T> = T & {
    isBuyer: boolean;
    isEnded: boolean;
    isAccepted: boolean;
    isExpired: boolean;
    uiStatus: NegotiationUiStatus;
    isSuccessful: boolean;
    isInProgress: boolean;
    isUnsuccessful: boolean;
};

type UseExtendedNegotiationReturn = {
    extendNegotiation: <T extends Pick<NegotiationEntity, 'state' | 'outcome' | 'buyerCompanyId'>>(
        negotiation: T,
        canActForSellerCompany?: boolean
    ) => ExtendedNegotiation<T>;
};

// A caller that already holds the scope ids passes them, sparing a second auth state per instance (one per listing card)
export const useExtendedNegotiation = (companyScopeIds = useAuthUtils().companyScopeIds): UseExtendedNegotiationReturn => {
    const extendNegotiation: UseExtendedNegotiationReturn['extendNegotiation'] = (negotiation, canActForSellerCompany) => {
        // With the sale detail at hand the side is the backend's: whoever can act for the selling company is the seller.
        // Without it (listing cards) the buying side is the whole group, not the one company the user sits in today.
        // Accepted limit of the cards: with buyer and seller in the same group this reads "buyer" for the seller too. Such
        // a negotiation cannot be opened at all (the access check refuses a buyer whose group includes the
        // selling company), so it only exists once companies merge or a person changes company afterwards.
        // Telling the two apart would need the selling company on the negotiation, which is not sent.
        const isBuyer = canActForSellerCompany === undefined ? companyScopeIds.value.includes(negotiation.buyerCompanyId) : !canActForSellerCompany;

        const isAccepted =
            !!negotiation.outcome && [NegotiationOutcome.AcceptedByBuyer, NegotiationOutcome.AcceptedBySeller].includes(negotiation.outcome);

        const isEnded = [NegotiationState.Closed, NegotiationState.Unpaid].includes(negotiation.state);

        const isExpired = negotiation.outcome === NegotiationOutcome.Expired;

        const uiStatus = ((): NegotiationUiStatus => {
            if (isAccepted && negotiation.state !== NegotiationState.Unpaid) return NegotiationUiStatus.Successful;
            if (negotiation.state === NegotiationState.Open) return NegotiationUiStatus.InProgress;

            return NegotiationUiStatus.Unsuccessful;
        })();

        return {
            isBuyer,
            isEnded,
            isAccepted,
            isExpired,
            uiStatus,
            isSuccessful: uiStatus === NegotiationUiStatus.Successful,
            isInProgress: uiStatus === NegotiationUiStatus.InProgress,
            isUnsuccessful: uiStatus === NegotiationUiStatus.Unsuccessful,
            ...negotiation,
        };
    };

    return {
        extendNegotiation,
    };
};
